import { selectEditorPreviewWidgetComponentProps, selectEditorSettingsColumnsOptions } from "../draft-selectors";
import { GroupOptions } from "../types";
import { getColDefFromColumnOptionState } from "../utils";
const deleteColumnDefinitionCorrespondingToField = (columns, field) => {
    if (!columns) {
        return;
    }
    let index = 0;
    let found = false;
    for (const column of columns) {
        if (column.field === field) {
            found = true;
            break;
        }
        if (column.children) {
            deleteColumnDefinitionCorrespondingToField(column.children, field);
        }
        index += 1;
    }
    if (found) {
        columns.splice(index, 1);
    }
};
const removeColumnDefinitionCorrespondingToFieldFromGroup = (columns, fields) => {
    if (!columns) {
        return columns;
    }
    const newColumns = [];
    for (const column of columns) {
        if (column.children) {
            let isGroupToDelete = false;
            for (const child of column.children) {
                if (child?.field && fields.includes(child.field)) {
                    newColumns.push(child);
                    isGroupToDelete = true;
                }
            }
            if (isGroupToDelete) {
                // Empty column children
                column.children = [];
            }
        }
    }
    return [...columns, ...newColumns];
};
const seekForFieldInColumns = (columns, field, headerNameForGroups, groupId) => {
    if (!field && !headerNameForGroups) {
        return null;
    }
    let correspondingCol = null;
    columns.forEach((c) => {
        if (c.field !== undefined && c.field === field) {
            correspondingCol = c;
        }
        if (c.groupId !== undefined && c.groupId === groupId) {
            correspondingCol = c;
        }
        if (c.children && headerNameForGroups !== null && headerNameForGroups === c.headerName && !correspondingCol) {
            correspondingCol = c;
        }
        if (c.children) {
            const childrenResult = seekForFieldInColumns(c.children, field, headerNameForGroups, groupId);
            if (childrenResult) {
                correspondingCol = childrenResult;
            }
        }
    });
    return correspondingCol;
};
const updateOrAddColDefToPreviewComponentProps = (componentProps, colDef) => {
    if (!componentProps.columnsTemplate?.columns) {
        componentProps.columnsTemplate = { ...componentProps.columnsTemplate, columns: [] };
    }
    const correspondingCol = seekForFieldInColumns(componentProps.columnsTemplate.columns, colDef.field, colDef.headerName, colDef.groupId);
    if (correspondingCol) {
        // Pinned and hide are not natural on groups ag-grid. We should force the value on children
        if (correspondingCol.children) {
            correspondingCol.children
                .forEach((c, index) => {
                updateOrAddColDefToPreviewComponentProps(componentProps, c);
                if (colDef.pinned) {
                    c.pinned = true;
                }
                if (colDef.hide) {
                    c.hide = true;
                }
                if (colDef.groupOption === GroupOptions.Collapsed && index !== 0) {
                    c.columnGroupShow = "open";
                }
                if (colDef.groupOption === GroupOptions.Expanded && index !== 0) {
                    c.columnGroupShow = "closed";
                }
            });
        }
        const newColumn = { ...correspondingCol, ...colDef };
        Object.assign(correspondingCol, newColumn);
    }
    else {
        componentProps.columnsTemplate.columns.push(colDef);
        componentProps.columnsTemplate.columns = componentProps.columnsTemplate.columns.sort((a, b) => a?.sequenceNumber - b?.sequenceNumber);
    }
};
const initColumnsTemplate = (state) => {
    const componentProps = selectEditorPreviewWidgetComponentProps(state);
    if (!componentProps?.columnsTemplate?.columns) {
        componentProps.columnsTemplate = { columns: [] };
    }
};
export const sortColumnTemplates = (state) => {
    const columnsTemplate = selectEditorPreviewWidgetComponentProps(state)?.columnsTemplate;
    if (!columnsTemplate || !columnsTemplate?.columns) {
        return;
    }
    columnsTemplate.columns = columnsTemplate.columns.sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0));
    columnsTemplate.columns.forEach((col) => {
        if (col.children) {
            col.children = col.children.sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0));
        }
    });
};
export const deleteColumnTemplate = (state, field) => {
    const columnOptions = selectEditorSettingsColumnsOptions(state);
    const componentProps = selectEditorPreviewWidgetComponentProps(state);
    if (!componentProps || !columnOptions) {
        return;
    }
    deleteColumnDefinitionCorrespondingToField(componentProps.columnsTemplate?.columns, field);
};
export const deleteEmptyGroupColumnTemplates = (state) => {
    const componentProps = selectEditorPreviewWidgetComponentProps(state);
    if (!componentProps || !componentProps.columnsTemplate?.columns) {
        return;
    }
    componentProps.columnsTemplate.columns = (componentProps.columnsTemplate?.columns).filter((d) => !d.children || d.children?.length !== 0);
};
export const deleteManyColumnTemplates = (state, fields) => {
    const columnOptions = selectEditorSettingsColumnsOptions(state);
    const componentProps = selectEditorPreviewWidgetComponentProps(state);
    if (!componentProps || !columnOptions || !componentProps.columnsTemplate?.columns) {
        return;
    }
    fields.forEach((f) => deleteColumnTemplate(state, f));
    deleteEmptyGroupColumnTemplates(state);
};
export const removeManyColumnTemplatesFromGroup = (state, fields) => {
    const columnOptions = selectEditorSettingsColumnsOptions(state);
    const componentProps = selectEditorPreviewWidgetComponentProps(state);
    if (!componentProps || !columnOptions || !componentProps.columnsTemplate?.columns) {
        return;
    }
    componentProps.columnsTemplate.columns = removeColumnDefinitionCorrespondingToFieldFromGroup(componentProps.columnsTemplate.columns, fields);
    deleteEmptyGroupColumnTemplates(state);
};
export const updateOrAddColumnTemplate = (state, columnOptionState) => {
    const componentProps = selectEditorPreviewWidgetComponentProps(state);
    if (componentProps) {
        updateOrAddColDefToPreviewComponentProps(componentProps, getColDefFromColumnOptionState(columnOptionState));
    }
};
export const updateOrAddManyColumnTemplates = (state, columnOptionStates) => {
    const componentProps = selectEditorPreviewWidgetComponentProps(state);
    if (!componentProps?.columnsTemplate?.columns) {
        initColumnsTemplate(state);
    }
    const groupColumns = columnOptionStates
        .filter((c) => c.isGroup)
        .map((c) => getColDefFromColumnOptionState(c, c.isGroup ? columnOptionStates.filter((o) => !o.isGroup && o.groupId === c.id) : null));
    const regularColumns = columnOptionStates
        .filter((c) => !c.isGroup && (c.groupId === null || c.groupId === undefined))
        .map((c) => getColDefFromColumnOptionState(c));
    const newColumns = [...regularColumns, ...groupColumns];
    newColumns.forEach((nc) => {
        updateOrAddColDefToPreviewComponentProps(componentProps, nc);
    });
    // To remove already grouped columns
    const groupedColumnsNames = columnOptionStates.filter((s) => s.groupId).map((s) => s.id)
        .filter((s) => s !== undefined);
    componentProps.columnsTemplate.columns = componentProps.columnsTemplate.columns.filter((s) => !groupedColumnsNames.includes(s.field));
    sortColumnTemplates(state);
};
